import { forkJoin, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { deserializeOfferGql, FiatWallet, Offer, Price, serializeOfferGql } from "@models";
import {
	AcceptOfferGQLService,
	AcceptOfferMutation,
	AcceptOfferRequest,
	CreateOfferGQLService,
	TradeType
} from "@gql/offer";
import { ExceptionsService } from "@core-services/exceptions.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { AcceptedOffer, AcceptedOfferDeserializer } from "@models/accepted-offer";
import { IPaymentOption, IPaymentOptionsFeeList } from "../entities/checkout.payment-options.model";
import { Helper, parseApiResponseArray2 } from "@helpers/functions";
import { IFiatDepositInfo, IFiatDepositInfoData } from "../entities/checkout.amount.model";
import {
	EstimatePricesGQLService,
	EstimatePricesQuery,
	EstimatePricesQueryVariables
} from "../../../api/graphql/offer/resources/generated/estimate.prices.service.graphql-gen";
import { mapEstimateIndexPricesToPrice, mapEstimatePricesToPrice } from "@modules/trade/helpers/helper";
import {
	EstimateIndexPricesGQLService,
	EstimateIndexPricesQuery,
	EstimateIndexPricesQueryVariables
} from "../../../api/graphql/offer/generated/estiamte-index.prices.service.graphql-gen";

@Injectable({ providedIn: "root" })
export class CheckoutRemoteService {
	public constructor(
		private apiService: ApicallService,
		private requestOfferGQLService: CreateOfferGQLService,
		private acceptOfferGQLService: AcceptOfferGQLService,
		private exceptionsService: ExceptionsService,
		private walletListService: TradableWalletListService,
		private estimatePricesService: EstimatePricesGQLService,
		private estimateIndexPricesService: EstimateIndexPricesGQLService
	) {}

	public getWalletDetailById(walletGuid: string): Observable<Price> {
		return this.apiService.get("wallet/" + walletGuid);
	}

	public getFiatWalletById(fiatId: string): Observable<FiatWallet> {
		return this.apiService.get("fiatwallet/" + fiatId);
	}

	public estimatePrices(input: EstimatePricesQueryVariables): Observable<EstimatePricesQuery["estimatePrices"]> {
		return this.estimatePricesService.fetch(input, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { estimatePrices } }) => estimatePrices),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	public estimateIndexPrices(
		input: EstimateIndexPricesQueryVariables
	): Observable<EstimateIndexPricesQuery["estimateIndexPrices"]> {
		return this.estimateIndexPricesService.fetch(input, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { estimateIndexPrices } }) => estimateIndexPrices),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	public getDefaultPriceRangeList(
		requestPayload: Offer,
		switchDefaultToGraphql: boolean = false,
		estimatePricesPayload?: EstimatePricesQueryVariables,
		isIndex: boolean = false
	): Observable<Price[]> {
		if (switchDefaultToGraphql) {
			const prices: Price[] = [];
			if (isIndex) {
				const payload: EstimateIndexPricesQueryVariables = {
					input: {
						type: TradeType.Buy,
						indexId: estimatePricesPayload?.input?.assetId,
						fiatId: estimatePricesPayload?.input?.fiatId
					}
				};
				return this.estimateIndexPrices(payload).pipe(
					map((price) => {
						prices.push(mapEstimateIndexPricesToPrice(price));
						return prices;
					})
				);
			}
			return this.estimatePrices(estimatePricesPayload).pipe(
				map((price) => {
					prices.push(mapEstimatePricesToPrice(price));
					return prices;
				})
			);
		}
		const option = new ApicallOption(Price, requestPayload);
		return this.apiService.post("trade/prices/default", option);
	}

	public createOfferGQL(offerData: Offer): Observable<Offer> {
		return this.requestOfferGQLService.mutate({ input: serializeOfferGql(offerData) }).pipe(
			map(({ data }) => deserializeOfferGql(data, offerData)),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	public acceptOfferGQL(input: AcceptOfferRequest, isDefault: boolean): Observable<Partial<AcceptedOffer>> {
		let isWalletNew = this.walletListService.getItemById(input.assetWalletId).isNew();

		// Only send walletId for non default wallets and not a new wallet
		if (isWalletNew || isDefault) {
			input.assetWalletId = null;
		}

		return this.acceptOfferGQLService.mutate({ input }).pipe(
			map(({ data }) => data.acceptOffer),
			switchMap((accepted) => this.fetchWalletsOnOfferAccepted(accepted, isWalletNew)),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Maps the response from the accept offer mutation to REST API calls to get the updated wallets so we can update the
	 * local cache in the component
	 *
	 * @param payload - an asset wallet id that was returned from the API when the offer was created
	 * @param fiatWalletId - a fiat wallet id that was returned from the API when the offer was created
	 * @param offerId - string
	 * @returns - an Observable containing a hashmap of AcceptedOffer
	 */
	private fetchWalletsOnOfferAccepted(
		payload: AcceptOfferMutation["acceptOffer"],
		isWalletNew: boolean
	): Observable<Partial<AcceptedOffer>> {
		if (!payload) {
			return of({});
		}

		const serialized = AcceptedOfferDeserializer.deserializeGraphQL(payload);

		if (!serialized?.walletId || !serialized?.fiatWalletId) {
			return of(serialized);
		}

		if (isWalletNew) {
			return this.apiService
				.get(`fiatwallet/${serialized?.fiatWalletId}`, new ApicallOption(FiatWallet))
				.pipe(map((result) => ({ ...serialized, fiatWallet: result?.[0] })));
		}

		const tradableWallet = this.walletListService.serviceForId(serialized?.walletId);

		return forkJoin([
			this.apiService.get(`wallet/${serialized?.walletId}`, new ApicallOption(tradableWallet.WalletType)),
			this.apiService.get(`fiatwallet/${serialized?.fiatWalletId}`, new ApicallOption(FiatWallet))
		]).pipe(
			map(([assetWallet, fiatWallet]) => ({
				...serialized,
				assetWallet: assetWallet?.[0],
				fiatWallet: fiatWallet?.[0]
			}))
		);
	}

	/**
	 * Get the payment options for the checkout page
	 * @returns Observable<IPaymentOption>
	 */
	public getPaymentOption(): Observable<IPaymentOption> {
		const option = new ApicallOption();
		option.apiVersion = "v3";
		// TODO: test with proper api error if it can be added back
		return this.apiService.get("deposit/paymentoptions", option).pipe(map((result) => ({ data: result })));
	}

	/**
	 * Get the payment options for the checkout page
	 * @param fiatId	string
	 * @param amount	number
	 * @returns Observable<Partial<IPaymentOptionsFeeList>[]>
	 */
	public getPaymentOptionFeeAttributes(
		fiatId: string,
		amount: number
	): Observable<Partial<IPaymentOptionsFeeList>[]> {
		const option = new ApicallOption();
		option.apiVersion = "v2";
		return this.apiService.get(`deposit/paymentoptions/${fiatId}/fee/${amount}`, option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((result) => parseApiResponseArray2<Partial<IPaymentOptionsFeeList>>(result))
		);
	}

	/**
	 * Get the payment options for the checkout page
	 * @returns Observable<IFiatDepositInfoData>
	 */
	public getDepositFiatAmounts$(): Observable<IFiatDepositInfoData> {
		const option = new ApicallOption();
		option.apiVersion = "v1";
		return this.apiService.get("deposit/fiats/amounts", option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((result) => ({ data: parseApiResponseArray2<IFiatDepositInfo>(result) }))
		);
	}
}
